import { useState, useEffect, useMemo, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  Box,
  CircularProgress,
} from '@mui/material';
import {
  Group,
  AirlineSeatReclineNormal,
  PersonOff,
  CheckCircleOutline,
  HighlightOff,
  FlightTakeoff,
  Work,
  FiberNew,
  Computer,
  AccessTime,
  GroupRemove,
  PersonRemove,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import ScrollToTop from 'components/ScrollToTop';
import StatCard from './StatCard';
import EmployeeCharts from './EmployeeCharts';
import RecentAdditions from './RecentAdditions';

const EmployeeStats = () => {
  useAuthCheck();
  const scrollRef = useRef(null);

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get('/employees')
      .then(({ data }) => {
        if (!cancelled) setEmployees(Array.isArray(data) ? data : data.data || []);
      })
      .catch(err => {
        if (!cancelled) toast.error(err.response?.data?.message || 'Failed to load employees.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const derived = useMemo(() => {
    const now = new Date();
    let total = 0, crew = 0, active = 0, crewActive = 0, nonCrewActive = 0;
    let newThisMonth = 0, office = 0, shift = 0, crewInactive = 0, nonCrewInactive = 0;
    const deptMap = {}, rankMap = {}, baseMap = {};

    for (const e of employees) {
      total++;
      const isCrew = e.employeeType === 'CREW';
      const isAct = !!e.isActive;

      if (isCrew) crew++;
      if (isAct) { active++; if (isCrew) crewActive++; else nonCrewActive++; }
      else { if (isCrew) crewInactive++; else nonCrewInactive++; }

      if (e.workingMode === 'OFFICE') office++;
      else if (e.workingMode === 'SHIFT') shift++;

      if (e.createdAt) {
        const d = new Date(e.createdAt);
        if (d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()) newThisMonth++;
      }

      const dept = e.department || 'Unknown';
      deptMap[dept] = (deptMap[dept] || 0) + 1;

      if (e.rank) rankMap[e.rank] = (rankMap[e.rank] || 0) + 1;

      const base = e.homeBase || 'Unknown';
      baseMap[base] = (baseMap[base] || 0) + 1;
    }

    const stats = {
      total, crew, nonCrew: total - crew,
      active, inactive: total - active,
      crewActive, nonCrewActive,
      newThisMonth, office, shift,
      crewInactive, nonCrewInactive,
    };

    return {
      stats,
      pieData: [
        { id: 'CREW',     label: 'CREW',     value: stats.crew },
        { id: 'NON-CREW', label: 'NON-CREW', value: stats.nonCrew },
      ],
      statusPieData: [
        { id: 'Active',   label: 'Active',   value: stats.active },
        { id: 'Inactive', label: 'Inactive', value: stats.inactive },
      ],
      barData:         Object.entries(deptMap).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([dept, count]) => ({ dept, count })),
      rankBarData:     Object.entries(rankMap).sort((a, b) => b[1] - a[1]).slice(0, 12).map(([rank, count]) => ({ rank, count })),
      homeBaseBarData: Object.entries(baseMap).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([base, count]) => ({ base, count })),
    };
  }, [employees]);

  const { stats, pieData, statusPieData, barData, rankBarData, homeBaseBarData } = derived;

  const recentEmployees = useMemo(() =>
    [...employees].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 5),
    [employees]
  );

  const STAT_ITEMS = [
    { icon: <Group fontSize="inherit" />,                    label: 'Total',             value: stats.total,           color: 'text.primary' },
    { icon: <AirlineSeatReclineNormal fontSize="inherit" />, label: 'Crew',              value: stats.crew,            color: 'primary.main' },
    { icon: <PersonOff fontSize="inherit" />,                label: 'Non-Crew',          value: stats.nonCrew,         color: 'text.secondary' },
    { icon: <CheckCircleOutline fontSize="inherit" />,       label: 'Active',            value: stats.active,          color: 'success.main' },
    { icon: <HighlightOff fontSize="inherit" />,             label: 'Inactive',          value: stats.inactive,        color: 'error.main' },
    { icon: <FiberNew fontSize="inherit" />,                 label: 'New / Month',       value: stats.newThisMonth,    color: 'secondary.main' },
    { icon: <FlightTakeoff fontSize="inherit" />,            label: 'Crew Active',       value: stats.crewActive,      color: 'info.main' },
    { icon: <Work fontSize="inherit" />,                     label: 'Non-Crew Active',   value: stats.nonCrewActive,   color: 'warning.main' },
    { icon: <GroupRemove fontSize="inherit" />,              label: 'Crew Inactive',     value: stats.crewInactive,    color: 'error.light' },
    { icon: <PersonRemove fontSize="inherit" />,             label: 'Non-Crew Inactive', value: stats.nonCrewInactive, color: 'error.dark' },
    { icon: <Computer fontSize="inherit" />,                 label: 'Office Mode',       value: stats.office,          color: 'info.light' },
    { icon: <AccessTime fontSize="inherit" />,               label: 'Shift Mode',        value: stats.shift,           color: 'warning.light' },
  ];

  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header title="Employee Stats" subtitle="Overview & Analytics" />
      </FlexBetween>

      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : (
        <Box ref={scrollRef} height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
          <Box display="flex" flexDirection="column" gap={2}>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(3,1fr)', md: 'repeat(6,1fr)' },
              gap: 2,
            }}>
              {STAT_ITEMS.map(s => <StatCard key={s.label} {...s} total={stats.total} />)}
            </Box>

            <EmployeeCharts
              pieData={pieData}
              statusPieData={statusPieData}
              barData={barData}
              rankBarData={rankBarData}
              homeBaseBarData={homeBaseBarData}
            />

            <RecentAdditions employees={recentEmployees} />

          </Box>
          <ScrollToTop scrollRef={scrollRef} />
        </Box>
      )}
    </Box>
  );
};

export default EmployeeStats;
